import type { Article } from "../article-types";

export const complianceOperationsArticles: Article[] = [
  {
    slug: "gst-registration-guide-australia",
    title: "GST registration in Australia: when, how and what changes",
    metaTitle: "GST Registration in Australia: When and How to Register",
    description:
      "When your business must register for GST in Australia, how to register, and what changes once you do: pricing, invoicing and BAS lodgment.",
    category: "Compliance",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "gst registration australia",
      "when to register for gst",
      "gst threshold 75000",
      "how to register for gst",
    ],
    intro:
      "Seventy five thousand dollars. That's the line that turns a hobby into a GST registered business, or a hundred and fifty thousand if you're running a not for profit. Cross it and registering with the ATO stops being optional, and so does everything bundled in behind it: ten per cent on taxable sales, tax invoices done properly, activity statements lodged on schedule. This one covers when registration becomes compulsory, how the process actually works, and what changes in your bookkeeping the day it happens.",
    sections: [
      { type: "h2", text: "When GST registration is compulsory" },
      {
        type: "p",
        text: "The trigger is GST turnover, broadly your gross business income minus GST itself and a handful of exclusions such as input taxed sales. The ATO tests it two ways. Look back over the current month plus the previous eleven, then look forward over the current month plus the next eleven; cross $75,000 on either measure and you're required to register, even if your financial year total sits comfortably under it.",
      },
      {
        type: "p",
        text: "It's the forward test that catches growing businesses off guard. Sign a contract that will clearly push you past the threshold over the next year, and the obligation can land before you've banked a cent of it. A handful of activities require registration regardless of turnover, taxi and ride sourcing services being the main example. The ATO lists the special cases, and a registered agent can usually tell you in five minutes whether any apply to you.",
      },
      {
        type: "ul",
        items: [
          "Your rolling twelve month turnover hits $75,000, or $150,000 for not-for-profits",
          "Your projected turnover for the next twelve months will reach the threshold",
          "You provide taxi or ride sourcing services, where registration applies from dollar one",
          "You want to claim fuel tax credits, which isn't possible without GST registration",
        ],
      },
      { type: "h2", text: "How to register" },
      {
        type: "p",
        text: "An ABN comes first, before anything else. From there, registration happens through the ATO's online services, through the same form used for the ABN application, or by having your tax or BAS agent lodge it for you. Two choices travel with it, a reporting cycle and an accounting basis, and both matter more than the dropdown menu makes them look.",
      },
      {
        type: "p",
        text: "Most small businesses end up on quarterly reporting and cash basis accounting, meaning GST is recognised when the money actually moves rather than when the invoice goes out. Cash basis tends to suit anyone paid after invoicing, since you're not left funding GST on sales you haven't collected yet. Accruals can suit other models better. Either way, it's a conversation worth having with your accountant or bookkeeper before you lock it in.",
      },
      { type: "h2", text: "What changes once you are registered" },
      {
        type: "p",
        text: "From your registration date, every taxable sale carries ten per cent GST, and your invoices need to meet the tax invoice rules: your ABN shown, the GST amount clear. The upside is you can claim back the GST on most of what you buy for the business, which softens the blow considerably.",
      },
      {
        type: "p",
        text: "Then there's the BAS, lodged each cycle. Quarterly lodgers work to fixed dates every year, and they don't move.",
      },
      {
        type: "table",
        headers: ["Quarter", "Period covered", "Standard due date"],
        rows: [
          ["Q1", "July to September", "28 October"],
          ["Q2", "October to December", "28 February"],
          ["Q3", "January to March", "28 April"],
          ["Q4", "April to June", "28 July"],
        ],
      },
      {
        type: "p",
        text: "A registered tax or BAS agent can often extend some of these dates. Monthly reporters don't get that luxury: they lodge by the 21st of the following month, full stop.",
      },
      {
        type: "callout",
        title: "Watch the rolling twelve months, not the financial year",
        text: "The $75,000 test doesn't reset on 30 June. It just keeps rolling, quietly, and a strong six months can tip you over mid year without you noticing. Once you're past roughly two thirds of the threshold, check your rolling turnover monthly. Registering late means owing GST on sales where you never charged a cent of it.",
      },
      { type: "h2", text: "Registering voluntarily" },
      {
        type: "p",
        text: "Nothing stops you registering below the threshold, and sometimes it's the smarter move. It suits businesses whose customers are mostly GST registered and will claim the credit back anyway, or anyone mid investment phase wanting to recover GST on big purchases. It makes far less sense selling to the public, since registering effectively adds ten per cent to your prices or eats ten per cent of your margin. Weigh the admin and cash flow cost against what you'd actually claim back before deciding either way.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "Getting registration right is the easy part. Running clean GST coding week after week is where most businesses drift. NextEra's managed monthly bookkeeping keeps sales and purchases coded properly so every BAS is right the first time, and a Strategic Finance Review is a sensible place to start if you're approaching the threshold and want it sorted before it turns urgent.",
      },
    ],
    faq: [
      {
        question: "What is the GST registration threshold in Australia?",
        answer:
          "$75,000 in GST turnover, or $150,000 for not-for-profits, tested on a rolling twelve month basis rather than against the financial year.",
      },
      {
        question: "Can I register for GST if I earn under $75,000?",
        answer:
          "Yes, voluntary registration is open at any turnover level. It's often worthwhile if your customers are GST registered businesses, since they'll claim the credit back regardless, or if you want to claim credits on a big purchase.",
      },
      {
        question: "What happens if I register for GST late?",
        answer:
          "The ATO can backdate your registration to when you should have registered, meaning you can end up owing GST on sales you never charged it on. Speak to a registered agent as soon as you suspect you're behind. The earlier it's sorted, the fewer surprises.",
      },
      {
        question: "Do I charge GST on everything once registered?",
        answer:
          "No, only on taxable sales; some items are GST-free or input taxed, and your invoicing needs to separate them correctly so your BAS reflects the right numbers.",
      },
    ],
    related: [
      "bas-due-dates-australia",
      "common-gst-mistakes",
      "tax-invoice-requirements-australia",
    ],
  },
  {
    slug: "ias-explained",
    title: "IAS explained: what it is and who lodges one",
    metaTitle: "IAS Explained: What It Is and Who Lodges One",
    description:
      "An IAS is an instalment activity statement used to report PAYG withholding and instalments outside a BAS. Learn who lodges one and when it is due.",
    category: "Compliance",
    date: "2026-07-26",
    readingMinutes: 5,
    keywords: ["ias explained", "instalment activity statement", "ias vs bas", "ias due date"],
    intro:
      "Think of an IAS as a BAS with the GST section quietly removed. It's the form the ATO issues when you've got PAYG amounts to report but a full BAS isn't required for that period: most commonly, employers remitting PAYG withholding monthly while their BAS stays quarterly, or taxpayers paying PAYG income tax instalments who aren't registered for GST. What follows is what it actually is, who gets one, and when it's due.",
    sections: [
      { type: "h2", text: "What an IAS actually is" },
      {
        type: "p",
        text: "Delete the GST section from a BAS and you've basically got an IAS: an activity statement lodged with the ATO reporting things like PAYG withholding from employee wages and PAYG income tax instalments. No GST means no complexity, usually just one or two figures to fill in.",
      },
      {
        type: "p",
        text: "Most of the confusion is naming, honestly. People hear BAS and IAS thrown around interchangeably and assume they're the same document. They're related, sure, but the IAS exists specifically for the periods or taxpayers where GST reporting doesn't come into it at all.",
      },
      { type: "h2", text: "Who lodges an IAS" },
      {
        type: "ul",
        items: [
          "Employers whose PAYG withholding is paid monthly while their BAS stays quarterly; they lodge an IAS for the first two months of each quarter, then report month three on the BAS",
          "Individuals or entities paying PAYG income tax instalments without a GST registration, such as some investors and trusts",
          "Businesses that deregistered for GST but still carry PAYG obligations",
          "Anyone the ATO has placed on monthly instalment reporting for other reasons",
        ],
      },
      {
        type: "p",
        text: "The two months on IAS, one month on BAS pattern is what trips people up most. Become a medium withholder because your annual withholding has grown, and your PAYG obligations go monthly while GST stays quarterly. Miss a monthly IAS in that setup and you fall behind fast.",
      },
      { type: "h2", text: "When an IAS is due" },
      {
        type: "p",
        text: "Monthly activity statements, IAS included, are generally due by the 21st of the following month. July's IAS, for instance, is due 21 August. Unlike quarterly BAS lodgments, the monthly deadline usually doesn't come with an extended agent date, which is exactly why it catches people out.",
      },
      {
        type: "p",
        text: "If your IAS just reports a pre-printed PAYG instalment amount and you're happy to pay it as issued, the whole process is as simple as paying by the due date. Want to vary the instalment because your income's changed? That takes more care, and it needs doing on time.",
      },
      {
        type: "callout",
        title: "PAYG withholding and PAYG instalments are different taxes",
        text: "Withholding is the tax taken out of employee wages and handed to the ATO on their behalf. Instalments are prepayments of your own income tax. Both can turn up on the same IAS, and mixing the two in your bookkeeping is one of the more common causes of messy balance sheets and BAS confusion.",
      },
      { type: "h2", text: "How the IAS fits into your bookkeeping rhythm" },
      {
        type: "p",
        text: "Treat the IAS like any other deadline: the figures should come straight out of a reconciled payroll and a clean ledger, not a last minute scramble. Payroll processed properly each cycle means the withholding figure already sits in your software, and lodgment takes minutes. Problems show up when corrections, bonuses or out of cycle pays haven't been recorded before the statement gets prepared.",
      },
      {
        type: "p",
        text: "It's also worth reconciling what's been reported across IAS and BAS against your payroll records at least once a quarter. Small mismatches add up quietly, and they tend to surface at year end when income statements get finalised.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "Monthly IAS obligations are exactly the sort of quiet deadline that slips when bookkeeping happens in fits and starts. NextEra's managed monthly bookkeeping keeps payroll reconciled and every activity statement built from clean data, and a Strategic Finance Review will quickly tell you whether your current lodgment cycle still fits the size of your business.",
      },
    ],
    faq: [
      {
        question: "What is the difference between a BAS and an IAS?",
        answer:
          "A BAS reports GST plus other obligations like PAYG withholding and instalments. An IAS reports those same PAYG amounts minus the GST section.",
      },
      {
        question: "When is an IAS due?",
        answer: "Generally by the 21st of the following month.",
      },
      {
        question: "Why do I lodge an IAS some months and a BAS in others?",
        answer:
          "It usually means your PAYG withholding is reported monthly while GST stays quarterly. You lodge an IAS for the first two months of the quarter, then wrap the third month into your BAS.",
      },
      {
        question: "Do sole traders lodge an IAS?",
        answer:
          "Some do. A sole trader paying PAYG income tax instalments without a GST registration may get an IAS rather than a BAS, though it depends entirely on your registrations and what the ATO has assessed. Worth confirming with your agent rather than guessing.",
      },
    ],
    related: [
      "bas-due-dates-australia",
      "payg-instalments-explained",
      "single-touch-payroll-guide-for-employers",
    ],
  },
  {
    slug: "payg-instalments-explained",
    title: "PAYG instalments explained for business owners",
    metaTitle: "PAYG Instalments Explained for Business Owners",
    description:
      "PAYG instalments are prepayments towards your income tax bill. Learn why the ATO enters you, how amounts are set and how to vary them safely.",
    category: "Compliance",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "payg instalments explained",
      "payg instalments business",
      "vary payg instalment",
      "payg instalment vs withholding",
    ],
    intro:
      "Nobody enjoys an unexpected letter from the ATO, and the one announcing you've entered the PAYG instalment system tends to land right after a good year, which feels almost unfair. It usually shows up once a lodged tax return reports business or investment income above the entry criteria. Once you're in, though, the instalments aren't an extra tax at all; they're prepayments credited against your assessment when the return goes in. Here's how the mechanics work, and how to manage them without wrecking your cash flow.",
    sections: [
      { type: "h2", text: "Why the ATO has entered you into PAYG instalments" },
      {
        type: "p",
        text: "Employees have tax withheld from every pay packet, so they meet their tax bill gradually across the year without really noticing. Business owners and investors don't get that luxury, which historically meant one large bill landing after the return was lodged. PAYG instalments spread that liability across the year instead. Once your return shows enough business or investment income, the ATO writes to you and starts issuing instalments, usually quarterly, on your activity statement.",
      },
      {
        type: "p",
        text: "The first year in the system often feels like a double hit, because you're paying last year's tax bill and prepaying this year's at the same time. That's a timing squeeze, not an error on anyone's part, and it's the main reason new entrants should plan their cash flow around it early rather than late.",
      },
      { type: "h2", text: "The two calculation methods" },
      {
        type: "p",
        text: "The ATO gives you two ways to work out each instalment, chosen on your activity statement.",
      },
      {
        type: "ul",
        items: [
          "Instalment amount: a fixed dollar figure calculated by the ATO from your last tax return, which you simply pay each period",
          "Instalment rate: the ATO gives you a percentage, applied to your actual instalment income for the period, so payments move up and down with revenue",
        ],
      },
      {
        type: "p",
        text: "The fixed amount is the simpler option, and plenty of business owners just pay it and move on. The rate method tracks reality more closely, which suits seasonal businesses or anyone with income that swings around, since a quiet quarter automatically produces a smaller instalment. The catch is you need reliable bookkeeping to know your instalment income each period, not a rough guess scribbled on the back of an envelope.",
      },
      { type: "h2", text: "Varying your instalments" },
      {
        type: "p",
        text: "If your income's genuinely dropped since the last return, you can vary the instalment down rather than overpaying and waiting on a refund at assessment time. Variations happen on the activity statement, before it's due.",
      },
      {
        type: "p",
        text: "Vary with care, though. Underestimate significantly and the ATO can apply interest on the shortfall. Base a variation on a realistic full year profit estimate, not on how the bank balance feels this week, and do it alongside your accountant or bookkeeper using actual year to date figures rather than gut feel.",
      },
      {
        type: "callout",
        title: "Instalments are your money coming back",
        text: "Every instalment you pay gets credited against your income tax assessment once the return is lodged. Pay more than your final liability and the difference comes back as a refund. Treat instalments as forced savings toward a bill you already know is coming, and the system starts working for you instead of against you.",
      },
      { type: "h2", text: "Managing instalments in your bookkeeping" },
      {
        type: "p",
        text: "Record instalments against an asset or clearing account for income tax paid in advance, never as an expense in the profit and loss. Coding them as an expense understates profit and creates confusion the moment the tax return gets prepared. Your bookkeeper should also reconcile what's been paid against your ATO account each quarter, so nothing goes missing or gets counted twice.",
      },
      {
        type: "p",
        text: "It's worth sanity checking the ATO's figure every year too. The instalment is based on your last lodged return, so a one off spike, a capital gain say, can inflate next year's instalments well past what you'll actually owe. That's a textbook case for a variation.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "Deciding whether to pay or vary an instalment needs accurate year to date profit figures, which happens to be exactly what managed monthly bookkeeping produces. NextEra keeps those numbers current so instalment decisions are made on evidence rather than instinct, and our Strategic Finance Review looks at how tax prepayments sit within your wider cash flow position.",
      },
    ],
    faq: [
      {
        question: "Are PAYG instalments an extra tax?",
        answer:
          "No. They're prepayments toward your current year's income tax bill, credited against your assessment when the return is lodged, with any excess refunded.",
      },
      {
        question: "Why did the ATO put me on PAYG instalments?",
        answer:
          "Because a lodged return showed business or investment income above the entry criteria, and the ATO enters you automatically once that happens.",
      },
      {
        question: "Can I reduce my PAYG instalments if income has dropped?",
        answer:
          "Yes, by varying the instalment on your activity statement before it's due. Base the variation on a realistic full year estimate, since a significant underestimate can attract interest, and it's worth doing with your accountant rather than alone.",
      },
      {
        question: "How are PAYG instalments different from PAYG withholding?",
        answer:
          "Withholding comes out of wages you pay employees and gets remitted on their behalf; instalments are prepayments of your own income tax. Plenty of businesses carry both on the same activity statement.",
      },
    ],
    related: ["ias-explained", "ato-payment-plans", "cash-flow-forecasting-guide"],
  },
  {
    slug: "ato-payment-plans",
    title: "Owing the ATO: how payment plans actually work",
    metaTitle: "ATO Payment Plans: How They Work for Businesses",
    description:
      "Behind on tax? How ATO payment plans work for businesses: setting one up, what the ATO expects, interest, and how to avoid defaulting.",
    category: "Compliance",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: ["ato payment plan", "owe the ato", "ato debt business", "ato payment arrangement"],
    intro:
      "Can't pay a tax debt in full? You're far from the only one, and the ATO will generally agree to a payment plan that spreads it over instalments, provided you engage early and keep lodging on time. Interest usually keeps accruing on the outstanding balance, and missing an instalment, or a new lodgment, can default the whole arrangement. Here's how these plans actually work, and how to set one up your cash flow can genuinely sustain rather than one that just sounds good on the phone.",
    sections: [
      { type: "h2", text: "First rule: lodge even if you cannot pay" },
      {
        type: "p",
        text: "Lodgment and payment are two separate obligations, not one. Skip lodging a BAS or return because you can't pay the bill, and you've turned one problem into two: late lodgment penalties on top, and a signal to the ATO that you've gone quiet. Lodging on time while asking for a payment arrangement is viewed far more favourably than silence. The debt doesn't get worse because you lodged. It gets worse because you hid.",
      },
      { type: "h2", text: "How a payment plan works" },
      {
        type: "p",
        text: "A payment plan is simply an agreement to clear a tax debt through regular instalments, typically an upfront amount followed by weekly, fortnightly or monthly payments. Smaller business debts can often be arranged online through the ATO or by your registered agent without a single phone call. Larger or older debts draw more scrutiny, and the ATO may ask about business viability, cash flow, and what you can realistically afford.",
      },
      {
        type: "p",
        text: "Interest generally keeps accruing on the unpaid balance while the plan runs, so a longer plan costs more overall. In limited hardship cases the ATO can remit interest, assessed case by case, and it's worth raising through your agent if genuine hardship applies to you.",
      },
      { type: "h2", text: "What the ATO expects while a plan is active" },
      {
        type: "ul",
        items: [
          "Every instalment paid on time, every time, no exceptions",
          "New lodgments, BAS and IAS included, in by their due dates",
          "New tax liabilities paid in full as they fall due, on top of the plan instalments",
          "Contact before a problem hits, not after a payment's already been missed",
        ],
      },
      {
        type: "p",
        text: "That third point sinks most plans. The instalments only cover the old debt; next quarter's BAS still needs paying in full alongside them. A plan that swallows every spare dollar leaves nothing for the next liability, and the cycle repeats with a bigger number attached to it.",
      },
      {
        type: "callout",
        title: "Set the instalment you can keep, not the one that sounds good",
        text: "It's tempting to promise an aggressive repayment just to end an uncomfortable phone call. A defaulted plan is worse than a slower one: the ATO gets harder to negotiate with next time, and stronger recovery action becomes more likely. Build the instalment from a realistic cash flow forecast that already accounts for your upcoming BAS, super and wage obligations.",
      },
      { type: "h2", text: "If a plan defaults" },
      {
        type: "p",
        text: "Miss an instalment or fail a new lodgment and the arrangement can be cancelled outright, making the full balance payable immediately. The ATO may allow a new plan, but goodwill thins fast after repeated defaults, and unresolved debts can escalate to garnishee notices, director penalty exposure on certain company debts, or disclosure of business tax debts in some circumstances. None of it arrives without warning, which is precisely why staying in contact matters so much.",
      },
      { type: "h2", text: "Fixing the cause, not just the symptom" },
      {
        type: "p",
        text: "A tax debt is usually a lagging indicator of something else. Common causes: GST and PAYG withholding spent as working capital because it was never set aside, pricing that doesn't cover the real cost base, or bookkeeping so far behind that liabilities ambush the owner every quarter. A separate account for GST, withholding and super, funded weekly, prevents most repeat debts. So does knowing your numbers monthly instead of quarterly.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "Businesses rarely fall behind with the ATO when the books are current and obligations are visible weeks ahead. NextEra's managed monthly bookkeeping keeps lodgments on time and liabilities forecast rather than discovered, and the Strategic Finance Review is a practical way to map a path out if tax debt has already built up.",
      },
    ],
    faq: [
      {
        question: "Can I get a payment plan with the ATO for my business?",
        answer:
          "Generally yes, if you engage early and the plan is realistic. Smaller debts can often be arranged online or through your agent; larger debts involve more assessment of your circumstances.",
      },
      {
        question: "Does interest still apply during an ATO payment plan?",
        answer: "Usually, yes, it keeps accruing on the outstanding balance.",
      },
      {
        question: "What happens if I miss a payment on my ATO plan?",
        answer:
          "The plan can default, making the full remaining balance payable straight away. Contact the ATO or your agent before a payment is missed if you possibly can. Renegotiating early beats recovering from a default every single time.",
      },
      {
        question: "Do I still lodge my BAS if I cannot pay it?",
        answer:
          "Yes, always. Lodgment and payment are separate obligations, and lodging on time avoids late lodgment penalties while keeping you in a stronger position to negotiate.",
      },
    ],
    related: [
      "payg-instalments-explained",
      "cash-flow-forecasting-guide",
      "bas-due-dates-australia",
    ],
  },
  {
    slug: "tax-invoice-requirements-australia",
    title: "Tax invoice requirements: what must be on an invoice",
    metaTitle: "Tax Invoice Requirements in Australia: What to Include",
    description:
      "What a valid tax invoice must show in Australia: seller details, ABN, GST amounts and more, plus why invalid invoices put your GST credits at risk.",
    category: "Compliance",
    date: "2026-07-26",
    readingMinutes: 5,
    keywords: [
      "tax invoice requirements australia",
      "what must be on a tax invoice",
      "valid tax invoice",
      "invoice abn gst",
    ],
    intro:
      "A valid tax invoice has to identify the seller, show their ABN, state the date, describe what was sold, and make the GST payable obvious, either as a separate figure or a statement that the total includes it. Higher value invoices need extra buyer identification too, and the ATO publishes the current threshold. None of this is decoration, however dull it looks on the page. Get it wrong and it's not just your GST credit at risk. It's your customer's.",
    sections: [
      { type: "h2", text: "Why the requirements exist" },
      {
        type: "p",
        text: "A tax invoice is the evidence base of the whole GST system. Claim a GST credit on a purchase, and the tax invoice is what substantiates it. Miss a required element and the ATO can deny the credit, which is why a surprising amount of BAS risk lives in something as unglamorous as invoice formatting.",
      },
      { type: "h2", text: "What a tax invoice must include" },
      {
        type: "ul",
        items: [
          "The words tax invoice, stated clearly, usually as the document title",
          "The seller's identity, generally the business name",
          "The seller's ABN",
          "The date the invoice was issued",
          "A description of what was sold, including quantity and price",
          "The GST amount payable, shown separately, or a clear statement that the total price includes GST, where GST is exactly one eleventh of the total",
          "The extent each sale is taxable, where the invoice mixes taxable and non-taxable items",
        ],
      },
      {
        type: "p",
        text: "Above a certain value, the buyer's identity or ABN needs to appear too. The ATO sets that threshold, worth confirming on their site, but the practical habit is simpler than remembering a number: put the customer's name on every invoice, and the question never comes up.",
      },
      {
        type: "p",
        text: "Very small purchases sit under a threshold where a full tax invoice isn't required to claim the credit, though you'll still want some record of the expense. Again, check the ATO's current figure rather than assuming it hasn't moved.",
      },
      { type: "h2", text: "Common ways invoices fail" },
      {
        type: "p",
        text: "Most failures are mundane. A quote or order confirmation gets waved through as an invoice. The ABN's missing because a template was copied from overseas software. GST isn't shown separately, and the total doesn't divide neatly into a GST component, leaving the amount ambiguous. Mixed supplies, an invoice combining GST-free items with taxable ones, show a single GST figure with no indication of which lines it actually applies to.",
      },
      {
        type: "p",
        text: "On the purchasing side, the risk flips. Get a document from a supplier with no ABN, and checking them on ABN Lookup takes ten seconds. Pay a supplier who can't quote an ABN, and you may need to withhold from the payment at the top rate. Not just a paperwork nicety, that one.",
      },
      {
        type: "callout",
        title: "Your accounting software is only as compliant as its setup",
        text: "Xero and similar platforms will produce compliant tax invoices out of the box, but only if your organisation settings carry the correct ABN, your invoice template says tax invoice, and your items carry the right tax rates. A ten minute template review catches problems that would otherwise sit quietly on every invoice you send.",
      },
      { type: "h2", text: "Recipient created tax invoices" },
      {
        type: "p",
        text: "In some industries the buyer issues the tax invoice instead of the seller, under a written agreement. Recipient created tax invoices turn up often in agriculture and similar arrangements where the buyer sets the value. They come with their own conditions, so if a customer proposes one, check it actually meets the ATO's requirements before relying on it.",
      },
      { type: "h2", text: "Keeping invoices as records" },
      {
        type: "p",
        text: "Tax invoices are business records, and records generally need keeping for five years for tax purposes. Digital copies are fine, provided they're true, clear reproductions. Build a habit of photographing or forwarding every supplier invoice into your accounting system the moment it arrives, and the substantiation already exists the day a credit is claimed rather than the day it's demanded.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "Invoice compliance is trivial once it's systemised, and expensive the moment it's ignored. NextEra's managed monthly bookkeeping keeps supplier documentation attached to transactions and your own templates compliant, and a Strategic Finance Review will surface any gaps in how sales and purchase records currently flow into your file.",
      },
    ],
    faq: [
      {
        question: "What must be on a tax invoice in Australia?",
        answer:
          "The words tax invoice, the seller's identity and ABN, the issue date, a description with quantity and price, and the GST amount or a statement that the total includes GST. Higher value invoices also need the buyer identified.",
      },
      {
        question: "Can I claim GST without a tax invoice?",
        answer:
          "For most purchases, no. Very small purchases under the ATO's threshold have relaxed requirements, but keep some record regardless.",
      },
      {
        question: "Is an invoice without an ABN valid?",
        answer:
          "No, it fails the tax invoice requirements. Paying a supplier who can't quote an ABN can also trigger a withholding obligation, so check them on ABN Lookup if you're at all unsure.",
      },
      {
        question: "How long do I need to keep tax invoices?",
        answer: "Generally five years for tax purposes. Clear digital copies are acceptable.",
      },
    ],
    related: [
      "gst-registration-guide-australia",
      "business-record-keeping-requirements",
      "common-gst-mistakes",
    ],
  },
  {
    slug: "business-record-keeping-requirements",
    title: "Business record keeping: what to keep and for how long",
    metaTitle: "Business Record Keeping: What to Keep and How Long",
    description:
      "Australian businesses generally must keep tax records for five years. What to keep, digital storage rules, and how to make record keeping automatic.",
    category: "Compliance",
    date: "2026-07-26",
    readingMinutes: 5,
    keywords: [
      "business record keeping requirements",
      "how long to keep business records australia",
      "ato record keeping",
      "digital records tax",
    ],
    intro:
      "If in doubt, keep it. Five years is the floor for how long Australian businesses generally need to keep tax records, in English or easily convertible, available the moment the ATO asks: income, expenses, GST, payroll, assets, all of it. Some employment records need holding longer under workplace law, so five years is a starting point rather than a universal answer, and it's rarely the interesting bit. What's actually interesting is what to keep, in what form, and how to make the whole thing run itself instead of running you.",
    sections: [
      { type: "h2", text: "The core rule" },
      {
        type: "p",
        text: "Records generally need keeping for five years for tax purposes, usually counted from when the record was prepared or obtained, or when the relevant transaction or return was finalised, whichever is later. In practice, disputes, carried forward losses and depreciating assets can all stretch how long a record actually stays relevant. Treat five years as the minimum, and hold onto anything connected to an open matter until it's genuinely closed.",
      },
      {
        type: "p",
        text: "A few categories run on separate rules entirely. Employee records under workplace law carry their own retention periods, which can exceed the tax rule; check Fair Work guidance or ask your adviser. Company records under corporations law bring their own obligations too.",
      },
      { type: "h2", text: "What to keep" },
      {
        type: "ul",
        items: [
          "Sales records: invoices issued, receipts, cash register or POS summaries",
          "Purchase and expense records: supplier tax invoices, receipts, contracts",
          "Banking records: statements for every business account, loan documents",
          "GST records: tax invoices, adjustment notes, activity statement workings",
          "Payroll records: pay runs, super contributions, PAYG withholding, employee details, timesheets",
          "Asset records: purchase documents, depreciation schedules, disposal records, some needing to be kept well beyond five years from purchase",
          "Stock records: stocktake workings and valuations",
          "Year end records: financial statements, reconciliations, tax return workpapers",
        ],
      },
      { type: "h2", text: "Paper or digital" },
      {
        type: "p",
        text: "Digital records are entirely acceptable, scans and photos of paper originals included, provided the copy is a true, clear reproduction and you can produce it on request. For most businesses that settles the question: capture everything digitally the moment it arrives, and stop wrestling shoeboxes.",
      },
      {
        type: "p",
        text: "The practical standard worth aiming for is every transaction in your accounting file carrying its source document attached. Modern platforms make this easy: email a supplier invoice to a capture address, or photograph a receipt, and the document lands against the transaction it supports. Five years of substantiation then exists as a by-product of ordinary bookkeeping, not a separate filing project someone has to remember to do at year end.",
      },
      {
        type: "callout",
        title: "The test is retrieval, not storage",
        text: "A record you can't find fails the requirement just as surely as one you never kept. Ask yourself: could you produce a supplier invoice from three years ago in five minutes, today? If not, the problem is your system, not your diligence. One capture point feeding one organised system beats a dozen folders, inboxes and glove boxes.",
      },
      { type: "h2", text: "What poor records actually cost" },
      {
        type: "p",
        text: "The obvious risk is an ATO review where deductions or GST credits can't be substantiated and get denied. The quieter costs land earlier: bookkeeping takes longer when documents are missing, questions bounce back and forth between you and your bookkeeper, year end fees climb because the accountant's reconstructing history, and decisions get made on numbers nobody fully trusts. Good records are cheaper than bad ones every single year, not just the year of an audit.",
      },
      { type: "h2", text: "Building a system that maintains itself" },
      {
        type: "p",
        text: "Pick one capture method per document type and make it a habit: supplier bills forwarded to the accounting inbox, receipts photographed at purchase, contracts saved to one drive location. Reconcile bank feeds weekly so missing documents get chased while the transaction's still fresh. Then leave retention to the software; cloud accounting platforms hold attached documents indefinitely, comfortably covering the five year requirement provided your subscription and backups stay current.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "Record keeping fails the moment it depends on willpower. NextEra's managed monthly bookkeeping bakes document capture and reconciliation into a fixed rhythm so substantiation builds up automatically, and our Strategic Finance Review includes a look at whether your current records would survive a review without a scramble.",
      },
    ],
    faq: [
      {
        question: "How long do I need to keep business records in Australia?",
        answer:
          "Generally five years for tax purposes, though some records, certain employee files and documents for long-held assets or open disputes among them, need holding longer. Treat five years as the floor, not the ceiling.",
      },
      {
        question: "Are digital copies of receipts acceptable to the ATO?",
        answer:
          "Yes, provided they're true and clear reproductions of the original and you can produce them if asked.",
      },
      {
        question: "What happens if I cannot produce records for a deduction?",
        answer:
          "The ATO can deny deductions or GST credits that can't be substantiated. Missing records also slow bookkeeping down and push up accounting costs long before any review ever happens, which is the part people tend to forget.",
      },
      {
        question: "Do records need to be kept in Australia or in English?",
        answer: "In English, or easily convertible, and accessible if the ATO asks.",
      },
    ],
    related: [
      "tax-invoice-requirements-australia",
      "receipt-capture-going-paperless",
      "eofy-checklist-business-owners",
    ],
  },
  {
    slug: "common-gst-mistakes",
    title: "Common GST coding mistakes and how to catch them",
    metaTitle: "Common GST Coding Mistakes and How to Catch Them",
    description:
      "The GST coding errors that quietly distort BAS lodgments: bank fees, wages, government charges and more, plus the checks that catch them early.",
    category: "Compliance",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "common gst mistakes",
      "gst coding errors",
      "bas mistakes small business",
      "gst on bank fees wages",
    ],
    intro:
      "Bank fees. Wages. Super. Government charges. Half the insurance bill. None of it carries GST, and yet all of it gets coded with GST somewhere, every month, in files that otherwise look perfectly tidy. Each error is small on its own. The trouble is it repeats monthly and flows straight into the BAS without anyone noticing. This one runs through the classic mistakes, why they keep happening, and the review habits that catch them before lodgment rather than during an ATO review.",
    sections: [
      { type: "h2", text: "Why GST coding goes wrong" },
      {
        type: "p",
        text: "GST looks binary, ten per cent or nothing, but the system actually runs on taxable, GST-free and input taxed categories, plus bills that mix components on a single line. Accounting software then applies whatever code got set once, right or wrong, to every future transaction through bank rules and defaults. One wrong default, multiplied by twelve months, is exactly how a small misunderstanding turns into a material BAS error.",
      },
      { type: "h2", text: "The classic mistakes" },
      {
        type: "ul",
        items: [
          "Claiming GST on bank fees and merchant charges, most of which are input taxed financial supplies with nothing to claim; some merchant service fees do include GST though, so check the statement rather than assuming either way",
          "Coding wages, superannuation and PAYG withholding with GST attached; payroll has no GST component at all",
          "Claiming GST on government charges such as ASIC fees, council rates, vehicle registration components and stamp duty",
          "Treating a whole insurance premium as creditable when part of the bill, stamp duty for instance, carries no GST",
          "Claiming GST on purchases from suppliers who aren't GST registered, where the invoice shows none because none was charged",
          "Miscoding overseas software subscriptions and imports; some overseas suppliers do charge Australian GST and some don't, so read the invoice rather than guessing",
          "Claiming a full GST credit on items with private use, instead of apportioning the business share",
          "Coding GST-free basic food, exports or medical services as taxable sales, which overstates GST collected",
        ],
      },
      { type: "h2", text: "Interest, loans and other traps" },
      {
        type: "p",
        text: "Loan repayments get a special mention here. The principal component isn't an expense at all, and the interest is input taxed, so nothing about a loan repayment belongs anywhere near a BAS as a creditable purchase. Transfers between your own accounts, owner drawings and dividend payments sometimes get scooped up as expenses with GST by overeager bank rules too. None of them belong in the GST report. Full stop.",
      },
      { type: "h2", text: "How to catch errors before they lodge" },
      {
        type: "p",
        text: "The fix isn't more care per transaction, it's a review layer that runs before every BAS. A structured pre-lodgment check takes well under an hour on a reasonably clean file, and catches nearly everything worth catching.",
      },
      {
        type: "p",
        text: "Run the GST audit or exception views in your software and scan for accounts pairing with tax rates that don't belong: any GST showing up on wages, super, bank fee or government charge accounts is an instant flag. Compare this period's GST collected against sales, which should sit close to one eleventh of GST-inclusive taxable sales; a drifting ratio means coding has shifted somewhere. Then check the bank rules directly, because fixing a bad rule fixes every future transaction, while fixing one transaction only fixes the one.",
      },
      {
        type: "callout",
        title: "Fix the rule, not just the transaction",
        text: "Find a miscoded item and the first question should always be what created it. If a bank rule or a default tax rate on an account or contact caused the error, fix the source and you've prevented the next hundred occurrences. Files that only ever fix individual transactions stay noisy forever.",
      },
      { type: "h2", text: "Correcting past mistakes" },
      {
        type: "p",
        text: "Finding historical errors is normal, and usually manageable. Many GST errors can be corrected on a later activity statement within limits the ATO sets, rather than amending old lodgments outright. The right path depends on the size and direction of the error, so quantify it first and let your BAS or tax agent choose how to fix it. What actually matters is fixing the cause at the same time, otherwise the same correction turns up again next quarter, and the quarter after that.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "Every NextEra file runs through a structured GST review before lodgment, and because the books are managed monthly, coding errors get caught within weeks rather than compounding across a year. Suspect your file's drifted? The Strategic Finance Review includes exactly this kind of coding health check.",
      },
    ],
    faq: [
      {
        question: "Do bank fees have GST in Australia?",
        answer:
          "Mostly no, most bank fees are input taxed financial supplies with nothing to claim. Some merchant service fees do include GST though, so check the statement rather than assuming.",
      },
      {
        question: "Should wages and super be coded with GST?",
        answer: "No. Wages, super and PAYG withholding carry no GST component whatsoever.",
      },
      {
        question: "What happens if I claimed GST incorrectly on past BAS lodgments?",
        answer:
          "Many errors can be corrected on a later activity statement within ATO limits, rather than amending old statements. Quantify the error first, then ask your BAS or tax agent for the right correction path, and fix whatever caused it so it doesn't just happen again next quarter.",
      },
      {
        question: "How do I check my file for GST coding errors?",
        answer:
          "Run your software's GST audit or exception reports before every BAS, look for tax rates paired with accounts that shouldn't have them, and check bank rules directly since one bad rule repeats the same error indefinitely.",
      },
    ],
    related: [
      "gst-registration-guide-australia",
      "chart-of-accounts-design",
      "xero-file-health-check",
    ],
  },
  {
    slug: "fuel-tax-credits-basics",
    title: "Fuel tax credits: the basics for eligible businesses",
    metaTitle: "Fuel Tax Credits: The Basics for Eligible Businesses",
    description:
      "Fuel tax credits refund part of the fuel tax in eligible business fuel use. Who can claim, what qualifies, and the records the ATO expects.",
    category: "Compliance",
    date: "2026-07-26",
    readingMinutes: 5,
    keywords: [
      "fuel tax credits",
      "fuel tax credit eligibility",
      "claim fuel tax credits bas",
      "fuel tax credits machinery",
    ],
    intro:
      "Every litre of fuel sold in Australia carries excise baked into the price before it ever reaches your tank. Fuel tax credits hand some of that back for eligible business use, claimed through the BAS. Machinery, plant, equipment and heavy vehicles generally qualify; a light vehicle pottering around on public roads generally doesn't. Rates vary by fuel type and activity and shift regularly, so always work off current ATO rates rather than what you used last quarter, and here's the rundown.",
    sections: [
      { type: "h2", text: "How the credit works" },
      {
        type: "p",
        text: "The policy logic is straightforward: for a lot of business fuel use, that excise isn't meant to apply, so the fuel tax credit system hands back some or all of it after the fact. You claim it on your BAS, where it reduces what you owe or adds to your refund.",
      },
      {
        type: "p",
        text: "To claim, you need to be registered for GST and for fuel tax credits, and the fuel has to be used in carrying on your business. The claim itself is calculated from the quantity of eligible fuel and the rate that applied to each activity when the fuel was acquired.",
      },
      { type: "h2", text: "What is generally eligible" },
      {
        type: "ul",
        items: [
          "Fuel used in machinery and equipment: excavators, generators, compressors, pumps",
          "Fuel used in vehicles operating off public roads, farms, construction sites and mine sites included",
          "Fuel used in heavy vehicles travelling on public roads, where the credit gets reduced by a road user charge",
          "Auxiliary equipment on heavy vehicles, refrigeration units for example, which can attract a different rate to the fuel propelling the vehicle",
        ],
      },
      {
        type: "p",
        text: "The main exclusion worth remembering is light vehicles on public roads: the everyday ute or van driving between jobs generally earns no credit for that on-road travel. Vehicle weight determines what counts as heavy, and the ATO publishes the threshold, so check there or ask your agent before assuming a vehicle qualifies.",
      },
      { type: "h2", text: "Rates change; do not hardcode them" },
      {
        type: "p",
        text: "Rates differ by fuel type and by activity, and they're adjusted regularly through the year. A spreadsheet built around last year's rate quietly produces wrong claims, every single time. The ATO's fuel tax credit calculator applies current rates and is the safest way to prepare each claim, particularly when a BAS period spans a rate change.",
      },
      {
        type: "callout",
        title: "Apportionment is where claims get defended or denied",
        text: "Most businesses run fuel across mixed activities: the same diesel fills a truck on the highway and powers equipment on site. Your claim needs to apportion litres between activities on a fair and reasonable basis, and your method is exactly what the ATO examines. Document how you split usage, odometer records, machine hours, telematics, or a documented percentage, and apply it consistently.",
      },
      { type: "h2", text: "Records that support a claim" },
      {
        type: "p",
        text: "You need records of the fuel acquired, tax invoices and fuel card statements mainly, plus records showing how it was used: logbooks, job records, equipment run sheets. Like other tax records, these generally need keeping for five years. Fuel card providers make the acquisition side easy; the usage side is where discipline pays off, particularly for heavy vehicle operators splitting on-road and off-road kilometres.",
      },
      { type: "h2", text: "Is it worth claiming" },
      {
        type: "p",
        text: "For businesses running significant machinery or heavy vehicles, the credits add up to real money, and skipping them is simply leaving margin on the table. For a business with one light vehicle and a lawn mower, the eligible litres are probably trivial, frankly. Never assessed it? A quick eligibility review against your fuel spend answers the question in minutes, and past periods can often be reviewed within the ATO's time limits for claiming.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "Fuel tax credits reward exactly what managed bookkeeping produces: clean fuel purchase records, a documented apportionment method, and claims calculated at current rates every BAS. NextEra builds this into the monthly rhythm for eligible clients, and the Strategic Finance Review is a good moment to check whether credits are being claimed correctly, or missed entirely.",
      },
    ],
    faq: [
      {
        question: "Who can claim fuel tax credits?",
        answer:
          "Businesses registered for both GST and fuel tax credits, for eligible fuel used in carrying on the business: machinery, off-road use and heavy vehicles among them. Light vehicles on public roads are generally excluded.",
      },
      {
        question: "How do I claim fuel tax credits?",
        answer:
          "On your BAS. Work out eligible litres by activity and apply the ATO's current rates, ideally using the ATO's fuel tax credit calculator since rates change regularly.",
      },
      {
        question: "Can I claim fuel tax credits for my ute or van?",
        answer:
          "Generally not for on-road travel, since light vehicles are excluded there, though off-road business use may qualify. Vehicle weight definitions matter here, so check the ATO guidance for your situation rather than guessing.",
      },
      {
        question: "What records do I need for fuel tax credits?",
        answer:
          "Records of fuel acquired and how it was used and apportioned, kept for five years.",
      },
    ],
    related: [
      "common-gst-mistakes",
      "bookkeeping-for-tradies",
      "business-record-keeping-requirements",
    ],
  },
  {
    slug: "eofy-checklist-business-owners",
    title: "EOFY preparation checklist for business owners",
    metaTitle: "EOFY Preparation Checklist for Business Owners",
    description:
      "A practical EOFY checklist for Australian business owners: reconciliations, payroll finalisation, super timing, stocktake and accountant handover.",
    category: "Operations",
    date: "2026-07-26",
    readingMinutes: 6,
    keywords: [
      "eofy checklist business",
      "end of financial year preparation",
      "eofy bookkeeping tasks",
      "stp finalisation checklist",
    ],
    intro:
      "Every June, the same scramble plays out in small businesses across the country: reconciliations left half done, a stocktake nobody scheduled, a payroll finalisation date that snuck up again. None of it needs to be dramatic. Reconcile every account, finalise payroll and Single Touch Payroll, sort out super timing, do a stocktake if you carry stock, review debtors and assets, and hand your accountant a clean file. Businesses that work through that list in June pay less in accounting fees and get their return done faster than the ones who start in August. Here's the full list, roughly in the order it should happen.",
    sections: [
      { type: "h2", text: "Reconcile everything first" },
      {
        type: "p",
        text: "Nothing else on this list means much until every bank account, credit card and loan in your file reconciles to its real world statement at 30 June. Clear the unpresented items that will never clear, chase missing source documents while suppliers can still resend them, and make sure clearing accounts, payroll and payment gateways especially, land back at their expected balances. A reconciled file is the foundation everything your accountant does gets built on.",
      },
      { type: "h2", text: "The payroll block" },
      {
        type: "p",
        text: "Payroll carries the hardest deadlines in EOFY, so give it its own project. Reconcile total wages, PAYG withholding and super in your payroll reports against the general ledger and against what's already been reported through Single Touch Payroll during the year. Then complete your STP finalisation declaration so employees' income statements go tax ready; it's due shortly after year end, and the ATO publishes the exact date.",
      },
      {
        type: "p",
        text: "Superannuation deserves its own attention here. The super guarantee sits at 12% of ordinary time earnings, and contributions are generally only deductible in the year the fund actually receives them, not the year they accrued. Want the deduction this year? Payments need to leave early enough to land before 30 June, clearing house processing time included.",
      },
      { type: "h2", text: "The core checklist" },
      {
        type: "ul",
        items: [
          "Reconcile all bank, credit card, loan and clearing accounts to 30 June",
          "Review aged receivables and write off genuinely bad debts before year end so the deduction lands this year",
          "Review aged payables and confirm the balances are real, not duplicates or stale entries",
          "Complete a stocktake at 30 June if you carry inventory, and record the closing valuation",
          "Reconcile payroll, PAYG withholding and super, then lodge your STP finalisation",
          "Update the asset register: additions, disposals, anything scrapped during the year",
          "Reconcile GST reported on BAS lodgments against the GST accounts in the ledger",
          "Confirm director loans and owner drawings are recorded correctly, and talk them through with your accountant",
          "Gather supporting documents: loan statements, new finance agreements, insurance schedules, major purchase invoices",
          "Back up or export year end reports, even though cloud files retain their own history",
        ],
      },
      {
        type: "callout",
        title: "June is for decisions; July is only for paperwork",
        text: "Most EOFY tax planning only works before 30 June: paying super early, writing off bad debts, scrapping obsolete stock or plant, timing purchases. Once the year ticks over, your accountant can only report what happened, not change it. A planning conversation in May or early June, with current numbers in front of you, beats any amount of effort spent in July.",
      },
      { type: "h2", text: "Handing over to your accountant" },
      {
        type: "p",
        text: "The best handover is a boring one: a reconciled file, a short note on anything unusual during the year, and supporting documents already attached to transactions or sitting in one folder. Every question your accountant doesn't have to ask saves time and fees. If the same questions keep coming up year after year, that's a sign the bookkeeping needs a structural fix, not another year of patching over it.",
      },
      { type: "h2", text: "After lodgment: close the loop" },
      {
        type: "p",
        text: "Once the accountant's finished, get their end of year adjusting journals and enter them into your own file so your software matches the lodged accounts. Skip this step and your file drifts further from reality every year, and comparative reporting stops meaning anything. Lock the closed period so the agreed figures can't shift underneath you later.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "For NextEra clients, EOFY is quiet by design: managed monthly bookkeeping means the file's already reconciled, and the checklist above is mostly confirmation rather than catch up work. If this June looked more like archaeology than admin, the Strategic Finance Review is the reset that stops it happening again next year.",
      },
    ],
    faq: [
      {
        question: "What should a business do before 30 June?",
        answer:
          "Reconcile all accounts, review debtors and write off bad debts, complete a stocktake if you hold inventory, pay super early enough for it to reach the fund if claiming the deduction this year, and have a tax planning conversation while decisions can still be made.",
      },
      {
        question: "When is STP finalisation due?",
        answer:
          "Shortly after the end of the financial year; the ATO publishes the exact date each year.",
      },
      {
        question: "Is super deductible if I pay it in July?",
        answer:
          "Generally, contributions are deductible in the year the fund receives them, not the year they accrued. To claim it this financial year, payments need to reach the fund by 30 June, so build in clearing house processing time.",
      },
      {
        question: "Do I need a stocktake at EOFY?",
        answer:
          "If you carry inventory, yes. Generally you need a closing stock figure at 30 June, and a physical stocktake is the standard way to get one, though some small businesses can use simplified rules; worth asking your accountant.",
      },
    ],
    related: [
      "new-financial-year-setup",
      "monthly-close-checklist",
      "single-touch-payroll-guide-for-employers",
    ],
  },
  {
    slug: "new-financial-year-setup",
    title: "Starting the new financial year clean",
    metaTitle: "Starting the New Financial Year Clean",
    description:
      "How to set up the new financial year properly: payroll updates, budgets, pricing, lodgment calendar and closing off the year just finished.",
    category: "Operations",
    date: "2026-07-26",
    readingMinutes: 5,
    keywords: [
      "new financial year setup",
      "new financial year checklist business",
      "july business checklist",
      "financial year planning",
    ],
    intro:
      "July doesn't feel like a fresh start until you actually treat it like one. Starting the new financial year clean means updating payroll for rate changes, locking last year's numbers, setting a budget, reviewing prices, and putting the year's lodgment deadlines into a calendar before the first one sneaks up on you. Call it half a day's work that pays for itself many times over. Here's a practical setup sequence for small business owners.",
    sections: [
      { type: "h2", text: "Close the old year before opening the new one" },
      {
        type: "p",
        text: "The single most valuable July habit is drawing a hard line under last year. Lock the closed period in your accounting software so June figures can't shift once your accountant starts work, and note whatever the file's still waiting on, final adjusting journals usually. When those arrive, enter them promptly so opening balances match the lodged accounts. Everything you measure this year is only as reliable as the baseline it starts from.",
      },
      { type: "h2", text: "Update payroll settings" },
      {
        type: "p",
        text: "The new financial year is when most payroll changes actually take effect. Award minimum wages get reviewed annually, and increases generally apply from the first full pay period in the new year, so check the awards covering your team and update pay templates before the first July pay run. Confirm your software's applied the current tax tables and that the super guarantee rate is correct; it currently sits at 12% of ordinary time earnings. Five minutes of checking now beats a year of back payments later.",
      },
      { type: "h2", text: "The setup checklist" },
      {
        type: "ul",
        items: [
          "Lock last financial year in your accounting software",
          "Check award rates and update employee pay templates from the first full pay period",
          "Confirm current tax tables and the 12% super guarantee rate are applied in payroll",
          "Load a budget for the new year into your accounting file so monthly reports show variance from day one",
          "Review your prices against last year's actual costs and margins",
          "Put every BAS, IAS, super and tax deadline for the year into your calendar now",
          "Review subscriptions, insurances and recurring costs before they auto-renew for another year",
          "Archive last year's documents into a clearly labelled year folder",
          "Tidy the chart of accounts: archive unused accounts, fix any coding workarounds you tolerated last year",
        ],
      },
      { type: "h2", text: "Budget and pricing: the two decisions that compound" },
      {
        type: "p",
        text: "A budget doesn't need to be elaborate. Start from last year's actuals, adjust for known changes, wage increases, rent reviews, planned hires, and load it into your software. The point was never prediction accuracy. It's that every monthly report for the next twelve months now shows where reality diverged from plan, which is where managing a business actually starts, rather than just watching it happen to you.",
      },
      {
        type: "p",
        text: "Pricing deserves the same annual discipline. Costs moved last year whether or not your prices did. Reviewing margins once a year, at a predictable time, with a full year of data in front of you, beats reacting mid year when cash feels tight. Even businesses holding prices steady should make that a decision, not a default nobody actually chose.",
      },
      {
        type: "callout",
        title: "Book the deadlines before the year gets busy",
        text: "Standard quarterly BAS due dates are 28 October, 28 February, 28 April and 28 July, monthly statements are generally due the 21st of the following month, and super has its own quarterly payment deadlines. Get them all into your calendar in July, with reminders a fortnight ahead. Most deadline stress in small business is really a calendar problem dressed up as a cash flow crisis.",
      },
      { type: "h2", text: "Set the operating rhythm, not just the settings" },
      {
        type: "p",
        text: "Settings decay without a rhythm behind them. Decide now what happens weekly and monthly: bank reconciliation weekly, a monthly close with a short report review, a quarterly sit down before each BAS. Write it down, assign each task an owner, even if every owner is you. A year of small consistent closes is what makes next year's EOFY genuinely uneventful.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "Most of this checklist is simply what NextEra's managed monthly bookkeeping does as standard: locked periods, payroll kept current, deadlines managed, monthly reports run against budget. If the new financial year feels like the right moment to stop doing this alone, the Strategic Finance Review is the natural first step.",
      },
    ],
    faq: [
      {
        question: "What should a business update at the start of the financial year?",
        answer:
          "Payroll first: award rate changes from the first full pay period, current tax tables, the correct super guarantee rate. Then lock last year, load a budget, review pricing and calendar the year's deadlines.",
      },
      {
        question: "When do award wage increases apply?",
        answer:
          "Generally from the first full pay period on or after the start date the Fair Work Commission announces each year.",
      },
      {
        question: "Why lock the previous financial year in my software?",
        answer:
          "Locking stops transactions in the closed year being added or edited after your accountant's prepared the return, keeping opening balances matched to the lodged accounts and your comparatives meaningful.",
      },
      {
        question: "Do I really need a budget for a small business?",
        answer:
          "A simple one, yes. Built from last year's actuals with known changes applied, it turns every monthly report into a variance report, arguably the cheapest management tool a small business has.",
      },
    ],
    related: [
      "eofy-checklist-business-owners",
      "budgets-owners-actually-use",
      "chart-of-accounts-design",
    ],
  },
  {
    slug: "monthly-close-checklist",
    title: "A monthly close checklist for small business",
    metaTitle: "A Monthly Close Checklist for Small Business",
    description:
      "A practical monthly close checklist for small business: reconciliations, payroll checks, GST review, reporting and locking the period.",
    category: "Operations",
    date: "2026-07-26",
    readingMinutes: 5,
    keywords: [
      "monthly close checklist",
      "month end bookkeeping process",
      "small business month end",
      "monthly bookkeeping routine",
    ],
    intro:
      "Without a monthly close, an accounting file is never actually finished. Numbers keep moving, nothing quite settles, and every report you pull carries a small asterisk nobody can see. A monthly close is the short, repeatable routine that fixes that: reconcile the accounts, check payroll and GST, produce a report you can actually trust, and lock the period behind you. Most small businesses get it down to a few hours once the habit sticks. Below is the routine, step by step, and why closing monthly beats leaving it all for BAS time or year end.",
    sections: [
      { type: "h2", text: "Why close monthly at all" },
      {
        type: "p",
        text: "Without a close, your accounting file stays permanently provisional: numbers shift as old transactions get recoded, and no report can ever be fully trusted. A monthly close turns the ledger into settled fact, one month at a time. Errors get caught weeks after they happen instead of a year later, BAS preparation becomes a review instead of a project, and the profit and loss becomes something you can actually steer the business with, not just look at.",
      },
      { type: "h2", text: "The checklist" },
      {
        type: "ul",
        items: [
          "Reconcile every bank account, credit card and loan account to its closing statement balance",
          "Clear suspense and unallocated transactions; nothing should be parked in limbo at close",
          "Check clearing accounts, payroll clearing and payment gateway accounts especially, return to nil or an explainable balance",
          "Review aged receivables: chase overdue invoices and flag the doubtful ones",
          "Review aged payables: confirm what's genuinely owed and remove duplicates",
          "Reconcile payroll for the month: wages, PAYG withholding and super accrued versus paid",
          "Scan the GST coding exceptions for the month while the transactions are still fresh",
          "Review the profit and loss line by line against last month and budget, and chase anything that looks off",
          "Glance over the balance sheet: negative balances and accounts that never move are both worth a question",
          "Attach any missing source documents identified during the review",
          "Lock the period in your software and file a short close note of anything unusual",
        ],
      },
      { type: "h2", text: "The review is the point, not the ticking" },
      {
        type: "p",
        text: "The mechanical steps only exist to make one thing possible: an actual look at the numbers each month. The profit and loss review is where an owner gets the real payoff. Revenue against last month and against budget, gross margin holding or slipping, any expense line that jumped. A question asked in week two of the month is worth ten asked at year end, because there's still time to do something about it.",
      },
      {
        type: "p",
        text: "The balance sheet review is shorter but catches different problems: a payroll liability growing because super's accruing but not being paid, a GST account that doesn't resemble the coming BAS, a loan balance that no longer matches the lender's statement. Most bookkeeping disasters announce themselves on the balance sheet months before they ever reach the profit and loss.",
      },
      {
        type: "callout",
        title: "Lock the period every single month",
        text: "Locking is the step most small businesses skip, and it's the one that makes everything else permanent. Leave a month open and it can be silently changed by a recoded transaction or an overenthusiastic bank rule, which means every report you've already produced from it is now wrong, and nobody knows it. Close it, lock it, let history stay history.",
      },
      { type: "h2", text: "Making the routine stick" },
      {
        type: "p",
        text: "Give the close a fixed slot early each month, before the new month's demands crowd it out, and keep the checklist written down rather than living in someone's head. The first few closes run slow because they surface accumulated mess; after that the workload drops sharply because each month starts clean. If a step keeps producing the same problem, fix the upstream cause, a bank rule, a missing document habit, rather than mopping up the symptom every month.",
      },
      { type: "h2", text: "Where NextEra fits" },
      {
        type: "p",
        text: "This checklist is essentially NextEra's managed monthly bookkeeping service written down: every client file reconciled, reviewed, reported and locked on a fixed monthly rhythm by a CPA-led team. If your own close keeps getting pushed back, the Strategic Finance Review will show what a properly closed file looks like against where yours actually sits today.",
      },
    ],
    faq: [
      {
        question: "What is a monthly close in bookkeeping?",
        answer:
          "A routine that reconciles all accounts, reviews payroll, GST and the key reports, then locks the period so the month's figures become final.",
      },
      {
        question: "How long should a monthly close take?",
        answer:
          "For a reasonably tidy small business, a few hours. The first few take longer while they clear accumulated issues, and after that the workload drops because each month starts clean.",
      },
      {
        question: "Why lock periods in accounting software?",
        answer:
          "Because it stops closed months being changed by later recoding or bank rules, which would silently invalidate reports and BAS figures already produced from them. It's the step that makes a close permanent, and skipping it is the single most common reason a close doesn't hold.",
      },
      {
        question: "What reports should I review at month end?",
        answer:
          "At minimum, the profit and loss against last month and budget, the balance sheet, aged receivables and aged payables.",
      },
    ],
    related: [
      "xero-file-health-check",
      "debtor-management-rhythm",
      "eofy-checklist-business-owners",
    ],
  },
];
